import { parseDateOnly } from "@nimit9/signet-lib/format"
import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Badge } from "@nimit9/signet-ui/components/badge"
import { Button } from "@nimit9/signet-ui/components/button"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nimit9/signet-ui/components/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nimit9/signet-ui/components/tooltip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useDateFormat } from "@/hooks/use-date-format"
import { type DetectedSubscription, useRecurring } from "@/hooks/use-recurring"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { api } from "@/lib/api"
import { cn } from "@/lib/cn"
import { buildSubscriptionsHeadline, isOverdue } from "./-headline"

export const Route = createFileRoute("/subscriptions/")({
  component: Subscriptions,
})

const FREQ_LABELS: Record<string, string> = {
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
}

const SECTION_LABEL = "text-label uppercase tracking-wider text-text-muted"

function ConfidenceDots({ value }: { value: number }) {
  const filled = Math.round(value * 5)
  const percent = Math.round(value * 100)
  const label = `Detection confidence: ${percent}% (${filled} of 5)`
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="flex gap-0.5 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={label}
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  i < filled ? "bg-accent-green" : "bg-bg-elevated",
                )}
              />
            ))}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Detection confidence</p>
          <p className="text-caption opacity-80">
            {percent}% — {filled} of 5
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** "Next" date; an overdue one is amber (at risk), never red. */
function NextDate({ sub }: { sub: DetectedSubscription }) {
  const formatDate = useDateFormat()
  const overdue = isOverdue(sub)
  return (
    <span
      data-overdue={overdue || undefined}
      className={cn(overdue && "font-medium text-accent-amber")}
    >
      {formatDate(parseDateOnly(sub.nextExpectedDate))}
      {overdue && " (overdue)"}
    </span>
  )
}

function MerchantCell({ sub }: { sub: DetectedSubscription }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {sub.category?.icon && <span className="text-callout">{sub.category.icon}</span>}
      <div className="min-w-0">
        <p className="truncate text-body font-medium text-text-primary">{sub.merchantName}</p>
        <p className="truncate text-caption text-text-muted">
          {sub.category?.name ?? "Uncategorised"} · {sub.transactionCount} payments
        </p>
      </div>
    </div>
  )
}

/** Phone: one stacked card per subscription. */
function SubscriptionCard({ sub }: { sub: DetectedSubscription }) {
  const formatDate = useDateFormat()
  return (
    <li
      data-testid="subscription-card"
      className="flex min-w-0 flex-col gap-2.5 rounded-xl bg-bg-surface p-4 ring-1 ring-border-subtle"
    >
      <div className="flex min-w-0 items-start justify-between gap-3">
        <MerchantCell sub={sub} />
        <div className="shrink-0 text-right">
          <Amount value={sub.averageAmount} className="text-body font-medium text-text-primary" />
          <p className="text-caption text-text-muted">{FREQ_LABELS[sub.frequency]}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-caption text-text-muted">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>Last: {formatDate(parseDateOnly(sub.lastDate))}</span>
          <span>
            Next: <NextDate sub={sub} />
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ConfidenceDots value={sub.confidence} />
          {!sub.isActive && <Badge variant="secondary">Stopped</Badge>}
        </div>
      </div>
    </li>
  )
}

/** Desktop: a real table, one row per subscription. */
function SubscriptionsTable({ subs }: { subs: DetectedSubscription[] }) {
  const formatDate = useDateFormat()
  return (
    <Table data-testid="subscriptions-table">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="pl-4">Merchant</TableHead>
          <TableHead>Frequency</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead>Last</TableHead>
          <TableHead>Next</TableHead>
          <TableHead className="pr-4">Confidence</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {subs.map((sub) => (
          <TableRow key={sub.merchantId} data-testid="subscription-row">
            <TableCell className="max-w-72 pl-4">
              <MerchantCell sub={sub} />
            </TableCell>
            <TableCell className="text-text-secondary">{FREQ_LABELS[sub.frequency]}</TableCell>
            <TableCell className="text-right">
              <Amount value={sub.averageAmount} className="font-medium text-text-primary" />
            </TableCell>
            <TableCell className="text-text-secondary">
              {formatDate(parseDateOnly(sub.lastDate))}
            </TableCell>
            <TableCell className="text-text-secondary">
              <NextDate sub={sub} />
            </TableCell>
            <TableCell className="pr-4">
              <div className="flex items-center gap-2">
                <ConfidenceDots value={sub.confidence} />
                {!sub.isActive && <Badge variant="secondary">Stopped</Badge>}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function Section({
  title,
  subs,
  isDesktop,
  muted = false,
}: {
  title: string
  subs: DetectedSubscription[]
  isDesktop: boolean
  muted?: boolean
}) {
  if (subs.length === 0) return null
  return (
    <section
      data-testid={`subscriptions-${title.toLowerCase()}`}
      className={cn("min-w-0", muted && "opacity-70")}
    >
      <p className={cn("mb-2", SECTION_LABEL)}>
        {title} ({subs.length})
      </p>
      {isDesktop ? (
        <div className="overflow-x-auto rounded-xl bg-bg-surface ring-1 ring-border-subtle">
          <SubscriptionsTable subs={subs} />
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {subs.map((sub) => (
            <SubscriptionCard key={sub.merchantId} sub={sub} />
          ))}
        </ul>
      )}
    </section>
  )
}

function Subscriptions() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useRecurring()
  const queryClient = useQueryClient()

  const syncMutation = useMutation({
    mutationFn: () => api.post("/recurring/sync", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["recurring"] })
      queryClient.invalidateQueries({ queryKey: ["merchants"] })
    },
  })

  const subs = useMemo(() => data?.data ?? [], [data])
  const totalMonthly = data?.totalMonthly ?? 0
  const headline = useMemo(
    () => buildSubscriptionsHeadline(subs, totalMonthly),
    [subs, totalMonthly],
  )

  if (isLoading) {
    return (
      <>
        <Header title="Subscriptions" showPeriod={false} />
        <PageWrapper>
          <div className="flex flex-col gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full animate-pulse rounded-xl" />
            ))}
          </div>
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Subscriptions" showPeriod={false} />
        <PageWrapper>
          <ErrorState
            message={error instanceof Error ? error.message : undefined}
            onRetry={refetch}
          />
        </PageWrapper>
      </>
    )
  }

  const active = subs.filter((s) => s.isActive)
  const stopped = subs.filter((s) => !s.isActive)

  return (
    <>
      <Header
        title="Subscriptions"
        showPeriod={false}
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? "Syncing…" : "Sync merchants"}
          </Button>
        }
      />
      <PageWrapper>
        <div
          data-testid="subscriptions-page"
          data-layout={isDesktop ? "desktop" : "phone"}
          className="flex min-w-0 flex-col gap-4 md:gap-5"
        >
          {headline && (
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          )}

          {active.length > 0 && (
            <StatStrip
              isDesktop={isDesktop}
              items={[
                { label: "Monthly burn", value: <Amount value={totalMonthly} /> },
                { label: "Active subscriptions", value: String(active.length) },
                { label: "Yearly estimate", value: <Amount value={totalMonthly * 12} /> },
              ]}
            />
          )}

          {syncMutation.isError && (
            <p role="alert" className="text-caption text-destructive">
              {syncMutation.error instanceof Error ? syncMutation.error.message : "Sync failed"}
            </p>
          )}

          {subs.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
              <p className="text-callout font-medium text-text-primary">
                No recurring payments detected
              </p>
              <p className="mt-1 text-body text-text-muted">
                Detection needs at least 3 payments to the same merchant. Upload a few more
                statements to see your subscriptions.
              </p>
            </div>
          ) : (
            <>
              <Section title="Active" subs={active} isDesktop={isDesktop} />
              <Section title="Stopped" subs={stopped} isDesktop={isDesktop} muted />
            </>
          )}
        </div>
      </PageWrapper>
    </>
  )
}
